import { useMemo } from "react";
import {
  InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  saveNote,
  getNotes,
  getNote,
  deleteNote,
  updateNote,
} from "../api/distributor/note";

export const noteKeys = {
  all: ["notes"] as const,
  list: (params?: { clientId?: number }) =>
    [...noteKeys.all, { ...params }] as const,
  details: () => ["note"] as const,
  detail: (id: number | null) => [...noteKeys.details(), { id }] as const,
  allTasks: () => ["note-tasks"] as const,
  tasks: (clientId?: number, date?: string) =>
    [...noteKeys.allTasks(), clientId, date] as const,
};

export function useSaveNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: saveNote,
    onSuccess: (response) => {
      const prepend = (
        data:
          | InfiniteData<{
              results: (typeof response)["note"][];
              next: string | null;
            }>
          | undefined
      ) => {
        if (!data) return data;
        const { note } = response;
        return {
          ...data,
          pages: data.pages.map((page, i) =>
            i === 0 ? { ...page, results: [note, ...page.results] } : page
          ),
        };
      };

      const { note } = response;

      queryClient.setQueryData(noteKeys.list(), prepend);
      if (note.client_id) {
        queryClient.setQueryData(
          noteKeys.list({ clientId: note.client_id }),
          prepend
        );
      }
    },
  });
}

export function useUpdateNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateNote,
    onSuccess: (data) => {
      queryClient.setQueryData(noteKeys.detail(data.id), data);

      type ListData = InfiniteData<{
        results: (typeof data)[];
        next: string | null;
      }>;

      const cachedLists = queryClient.getQueriesData<ListData>({
        queryKey: noteKeys.all,
      });
      const oldClientId = cachedLists
        .flatMap(([, list]) => list?.pages.flatMap((p) => p.results) ?? [])
        .find((n) => n.id === data.id)?.client_id;

      const replace = (existing: ListData | undefined) => {
        if (!existing) return existing;
        return {
          ...existing,
          pages: existing.pages.map((page) => ({
            ...page,
            results: page.results.map((note) =>
              note.id === data.id ? data : note
            ),
          })),
        };
      };

      const remove = (existing: ListData | undefined) => {
        if (!existing) return existing;
        return {
          ...existing,
          pages: existing.pages.map((page) => ({
            ...page,
            results: page.results.filter((note) => note.id !== data.id),
          })),
        };
      };

      const prepend = (existing: ListData | undefined) => {
        if (!existing) return existing;
        return {
          ...existing,
          pages: existing.pages.map((page, i) =>
            i === 0 ? { ...page, results: [data, ...page.results] } : page
          ),
        };
      };

      queryClient.setQueryData(noteKeys.list(), replace);

      if (oldClientId !== data.client_id) {
        if (oldClientId) {
          queryClient.setQueryData(
            noteKeys.list({ clientId: oldClientId }),
            remove
          );
        }
        if (data.client_id) {
          queryClient.setQueryData(
            noteKeys.list({ clientId: data.client_id }),
            prepend
          );
        }
      } else if (data.client_id) {
        queryClient.setQueryData(
          noteKeys.list({ clientId: data.client_id }),
          replace
        );
      }
    },
  });
}

export function useNotes(
  params?: { clientId?: number },
  options?: { enabled?: boolean }
) {
  const response = useInfiniteQuery({
    queryKey: noteKeys.list(params),
    queryFn: ({ pageParam }) => getNotes({ ...params, page: pageParam }),
    getNextPageParam: (lastPage) =>
      lastPage.next
        ? Number(lastPage.next.match(/page=(\d+)/)?.[1])
        : undefined,
    initialPageParam: 1,
    enabled: options?.enabled ?? true,
  });
  const notes = useMemo(
    () => response.data?.pages.flatMap((p) => p.results) ?? [],
    [response.data]
  );
  return { ...response, notes };
}

export function useNote(id: number, enabled?: boolean) {
  return useQuery({
    queryKey: noteKeys.detail(id),
    queryFn: () => getNote(id),
    enabled,
  });
}

export function useFetchNote() {
  return useMutation({
    mutationFn: getNote,
  });
}

export function useDeleteNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteNote,
    onSuccess: (_response, id) => {
      type ListData = InfiniteData<{
        results: { id: number }[];
        next: string | null;
      }>;

      queryClient.setQueriesData<ListData>(
        { queryKey: noteKeys.all },
        (existing) => {
          if (!existing) return existing;
          return {
            ...existing,
            pages: existing.pages.map((page) => ({
              ...page,
              results: page.results.filter((note) => note.id !== id),
            })),
          };
        }
      );

      queryClient.removeQueries({ queryKey: noteKeys.detail(id) });
    },
  });
}
